import * as THREE from 'three';
import { PERSON_LANDMARKS as P } from './anatomy.js';
import { PersonDiffuseMaterial } from './lighting.js';

// CPU counterpart is used by geometry/pose validation. Rendering evaluates the
// same two-bone blend on the GPU, with one float per visible instance.
export function deformPersonVertex(out, point, mode, amount) {
  out.copy(point);
  if (!amount) return out;
  if (mode==='cloth') {
    const d=Math.max(0,1.05-point.y), u=Math.min(d/.04,1);
    const t=u*u*(3-2*u)*amount;
    // Integral of smoothstep: its derivative stays <= 1, avoiding an inverted
    // fold at the waist while the hem settles over the thighs.
    const f=d<.04?.04*(u**3-.5*u**4):d-.02;
    out.y+=f*.98*amount;
    out.z=point.z*(1-.20*t)+f*.25*amount;
  } else {
    const joint=mode==='arm'?P.elbowY:P.kneeY;
    const w=1-THREE.MathUtils.smoothstep(point.y,joint-.065,joint+.065);
    const y=point.y-joint,c=Math.cos(amount),s=Math.sin(amount);
    out.y=THREE.MathUtils.lerp(point.y,joint+c*y-s*point.z,w);
    out.z=THREE.MathUtils.lerp(point.z,s*y+c*point.z,w);
  }
  return out;
}

export class PersonDeformMaterial extends PersonDiffuseMaterial {
  constructor(mode='arm') {
    super({color:0xffffff,vertexColors:true});
    this.personMode=mode;
    this.personDeform={value:0};
  }
  copy(source) {
    super.copy(source); this.personMode=source.personMode;
    this.personDeform={value:source.personDeform.value}; return this;
  }
  customProgramCacheKey() { return super.customProgramCacheKey()+'-deform-v1-'+this.personMode; }
  onBeforeCompile(shader) {
    super.onBeforeCompile(shader);
    shader.uniforms.personDeformUniform=this.personDeform;
    const cloth=this.personMode==='cloth',joint=String(this.personMode==='arm'?P.elbowY:P.kneeY);
    shader.vertexShader=shader.vertexShader.replace('#include <common>',`#include <common>
      #ifdef USE_INSTANCING
        attribute float personDeform;
      #else
        uniform float personDeformUniform;
      #endif
      float personAmount() {
        #ifdef USE_INSTANCING
          return personDeform;
        #else
          return personDeformUniform;
        #endif
      }
      vec3 personWarp(vec3 p) {
        float amount=personAmount();
        ${cloth?`float d=max(0.0,1.05-p.y),u=min(d/.04,1.0),t=u*u*(3.0-2.0*u)*amount;
          float f=d<.04?.04*(u*u*u-.5*u*u*u*u):d-.02;
          return vec3(p.x,p.y+f*.98*amount,p.z*(1.0-.20*t)+f*.25*amount);`
          :`float w=1.0-smoothstep(${joint}-.065,${joint}+.065,p.y);
          float c=cos(amount),s=sin(amount),y=p.y-(${joint});
          return mix(p,vec3(p.x,${joint}+c*y-s*p.z,s*y+c*p.z),w);`}
      }
      vec3 personNormal(vec3 p,vec3 n) {
        float amount=personAmount();
        if(abs(amount)<.000001)return n;
        // Cofactor of the exact YZ Jacobian: preserves normals through the blend
        // without extra neighboring-vertex evaluations or finite-difference noise.
        float A,B,C,D;
        ${cloth?`float d=max(0.0,1.05-p.y),u=min(d/.04,1.0),t=u*u*(3.0-2.0*u)*amount;
          float dt=(u>0.0&&u<1.0)?6.0*u*(1.0-u)/.04*amount:0.0;
          A=1.0-.98*t;B=0.0;C=.20*p.z*dt-.25*t;D=1.0-.20*t;`
          :`float u=clamp((p.y-(${joint}-.065))/.13,0.0,1.0),w=1.0-u*u*(3.0-2.0*u);
          float dw=(u>0.0&&u<1.0)?-6.0*u*(1.0-u)/.13:0.0;
          float c=cos(amount),s=sin(amount),y=p.y-(${joint});
          A=1.0+w*(c-1.0)+dw*(${joint}+c*y-s*p.z-p.y);B=-w*s;
          C=w*s+dw*(s*y+c*p.z-p.z);D=1.0+w*(c-1.0);`}
        return normalize(vec3(n.x*(A*D-B*C),D*n.y-C*n.z,-B*n.y+A*n.z));
      }`);
    shader.vertexShader=shader.vertexShader.replace('#include <beginnormal_vertex>',
      '#include <beginnormal_vertex>\n objectNormal=personNormal(position,objectNormal);');
    shader.vertexShader=shader.vertexShader.replace('#include <begin_vertex>',
      '#include <begin_vertex>\n transformed=personWarp(transformed);');
  }
}

export function personDepthMaterial(surface) {
  const depth=new THREE.MeshDepthMaterial({depthPacking:THREE.RGBADepthPacking});
  depth.onBeforeCompile=shader=>surface.onBeforeCompile(shader);
  depth.customProgramCacheKey=()=>`person-depth-v1-${surface.personMode}`;
  return depth;
}
